import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box, Chip, Divider, IconButton, InputAdornment, Paper, Stack,
    TextField, Tooltip, Typography,
} from "@mui/material";
import dayjs from "dayjs";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import AssignmentReturnedRoundedIcon from "@mui/icons-material/AssignmentReturnedRounded";

import BaseCalendar from "../../component/calendar/BaseCalendar";
import FormModal from "../../component/common/FormModal";
import FormField from "../../component/common/FormField";
import ComboBox from "../../component/common/ComboBox";
import ActionButton from "../../component/common/ActionButton";
import LoadingDots from "../../component/common/LoadingDots";
import SearchField, { searchMatch } from "../../component/common/SearchField";
import { equipmentApi } from "./api/equipmentApi";
import { useMyPermissions } from "../../util/useMyPermissions";
import { runApiAction } from "../../util/apiFeedback";
import { confirm as confirmDialog } from "../../util/confirmDialog";
import { formatDate } from "../../util/dateUtils";
import { ds } from "../../styles/designSystem";

const C = ds.colors;   // 색상은 디자인 토큰에서만 (하드코딩 금지)
// 비품 예약 강조색 — 표준 캘린더의 '비품 예약'(청록)과 동일. 도메인 전용 액센트라 로컬 상수로 둔다.
const RESV_COLOR = { bg: "#cffafe", bar: ds.finance.cumIncome, text: "#164e63" };   // 표준 캘린더와 동일(청록)
const RESV_RETURNED_COLOR = { bg: "#eef2f6", bar: "#94a3b8", text: "#64748b" };     // 반납완료 = 회색(비활성)
const CAT_PALETTE = [
    { bg: "#eff6ff", fg: "#1e40af" }, { bg: "#f0fdf4", fg: "#15803d" },
    { bg: "#fef2f2", fg: "#b91c1c" }, { bg: "#fffbeb", fg: "#b45309" },
    { bg: "#ede9fe", fg: "#6d28d9" }, { bg: "#ecfeff", fg: "#0e7490" },
    { bg: "#fdf2f8", fg: "#be185d" },
];
const catColor = (id) => CAT_PALETTE[Math.abs(id ?? 0) % CAT_PALETTE.length];

const todayStr = () => formatDate(new Date());
const emptyItem = () => ({ equipmentId: "", quantity: 1 });
const emptyCart = () => ({
    projectName: "", projectChannelId: null, deptId: "", applicantName: "",
    startDate: todayStr(), endDate: todayStr(), memo: "", items: [emptyItem()],
});
const emptyEquip = () => ({ id: null, name: "", categoryId: "", locationId: "", quantity: 1, memo: "" });

/**
 * 비품 예약 — 한 화면: 중앙 캘린더(예약 현황) + 우측 비품 목록.
 * 누구나 비품을 조회하고 예약할 수 있으며, EQUIPMENT_MANAGE 권한자는 비품·카테고리·보관위치를 관리한다.
 * 예약은 표준 캘린더(직원 캘린더)에도 청록색 일정으로 표시된다.
 */
export default function EquipmentPage() {
    const { hasPerm } = useMyPermissions();
    const canManage = hasPerm("EQUIPMENT_MANAGE");

    const [anchor, setAnchor]         = useState(dayjs());
    const [categories, setCategories] = useState([]);
    const [locations, setLocations]   = useState([]);
    const [equipment, setEquipment]   = useState([]);
    const [calReservations, setCalReservations] = useState([]);   // 캘린더 창(기간 한정) 예약 — 전체 로드 안 함
    const [departments, setDepartments]   = useState([]);
    const [channels, setChannels]     = useState([]);
    const [me, setMe]                 = useState(null);
    const [loading, setLoading]       = useState(true);

    const [catFilter, setCatFilter]   = useState("");   // 우측 목록 카테고리 필터
    const [search, setSearch]         = useState("");

    // 다이얼로그 상태
    const [booking, setBooking]       = useState(null);  // null = 닫힘 (예약건 상세/수정 — 그룹 단위)
    const [resvCart, setResvCart]     = useState(null);  // null = 닫힘 (여러 비품 예약 장바구니)
    const [cartAvail, setCartAvail]   = useState({});    // 장바구니 줄별 가용수량 { [idx]: number|null }
    const [bookingAvail, setBookingAvail] = useState({});   // 예약건 편집 줄별 가용수량 { [idx]: number|null }
    const [equipForm, setEquipForm]   = useState(null);
    const [catalogOpen, setCatalogOpen] = useState(false);
    const [saving, setSaving]         = useState(false);

    const loadMasters = useCallback(() => {
        setLoading(true);
        Promise.allSettled([
            equipmentApi.getCategories(), equipmentApi.getLocations(),
            equipmentApi.getEquipment(),
            equipmentApi.getDepartments(), equipmentApi.getChannels(), equipmentApi.getMe(),
        ]).then(([c, l, e, d, ch, m]) => {
            if (c.status === "fulfilled") setCategories(c.value || []);
            if (l.status === "fulfilled") setLocations(l.value || []);
            if (e.status === "fulfilled") setEquipment(e.value || []);
            if (d.status === "fulfilled") setDepartments(d.value || []);
            if (ch.status === "fulfilled")
                setChannels((ch.value || []).filter(x => x.type === "PROJECT"));
            if (m.status === "fulfilled") setMe(m.value || null);
        }).finally(() => setLoading(false));
    }, []);

    useEffect(() => { loadMasters(); }, [loadMasters]);

    // 캘린더는 보이는 달 ±1주만 조회 — 전체 예약을 로드하지 않아 데이터가 늘어도 안전
    const loadCalendar = useCallback(() => {
        const from = anchor.startOf("month").subtract(7, "day").format("YYYY-MM-DD");
        const to   = anchor.endOf("month").add(7, "day").format("YYYY-MM-DD");
        equipmentApi.getReservationCalendar(from, to).then(d => setCalReservations(d || [])).catch(() => {});
    }, [anchor]);
    useEffect(() => { loadCalendar(); }, [loadCalendar]);

    // 예약 변경 후 — 캘린더 + 비품 목록(오늘 예약수량은 목록 API가 계산) 갱신
    const reloadReservations = useCallback(() => {
        loadCalendar();
        equipmentApi.getEquipment().then(d => setEquipment(d || [])).catch(() => {});
    }, [loadCalendar]);
    const reloadCatalog = useCallback(() => {
        equipmentApi.getCategories().then(d => setCategories(d || [])).catch(() => {});
        equipmentApi.getLocations().then(d => setLocations(d || [])).catch(() => {});
        equipmentApi.getEquipment().then(d => setEquipment(d || [])).catch(() => {});
    }, []);

    // ─── 캘린더 spans — 예약건(groupId)당 하나의 바로 묶음 ─────────
    const spans = useMemo(() => {
        const groups = new Map();   // groupId → 같은 예약건 행들
        for (const r of calReservations) {
            const key = r.groupId || `single-${r.id}`;
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(r);
        }
        return Array.from(groups.entries()).map(([key, rows]) => {
            const allReturned = rows.every(x => x.returned);
            const col = allReturned ? RESV_RETURNED_COLOR : RESV_COLOR;
            const first = rows[0];
            const primary = rows.map(x => x.equipmentName).find(Boolean) || "비품";
            const itemLabel = rows.length > 1 ? `${primary} 외 ${rows.length - 1}건` : primary;
            const base = first.projectName ? `${itemLabel} · ${first.projectName}` : itemLabel;
            const start = rows.reduce((m, x) => (x.startDate < m ? x.startDate : m), first.startDate);
            const end   = rows.reduce((m, x) => (x.endDate   > m ? x.endDate   : m), first.endDate);
            return {
                id: `grp-${key}`,
                start, end,
                label: allReturned ? `✓ ${base} (반납)` : base,
                bg: col.bg, bar: col.bar, text: col.text,
                onClick: () => openBooking(rows),
            };
        });
    }, [calReservations]);  // eslint-disable-line react-hooks/exhaustive-deps

    // ─── 우측 비품 목록 (필터) ────────────────────────────────────
    const filteredEquipment = useMemo(() => equipment.filter(e =>
        (!catFilter || String(e.categoryId) === String(catFilter))
        && searchMatch(search, [e.name, e.categoryName, e.locationName, e.memo])
    ), [equipment, catFilter, search]);

    // 장바구니: 줄별(비품·기간) 가용 수량 조회. 같은 비품 중복 줄의 합산 초과는 저장 시 서버가 최종 검증.
    const cartKey = resvCart
        ? `${resvCart.startDate}|${resvCart.endDate}|${resvCart.items.map(i => i.equipmentId).join(",")}`
        : "";
    useEffect(() => {
        if (!resvCart || !resvCart.startDate) { setCartAvail({}); return; }
        let alive = true;
        Promise.all(resvCart.items.map(it =>
            it.equipmentId
                ? equipmentApi.getAvailability(it.equipmentId, resvCart.startDate, resvCart.endDate || resvCart.startDate)
                    .then(d => d?.available ?? 0).catch(() => null)
                : Promise.resolve(null)
        )).then(arr => { if (alive) setCartAvail(Object.fromEntries(arr.map((v, i) => [i, v]))); });
        return () => { alive = false; };
    }, [cartKey]);  // eslint-disable-line react-hooks/exhaustive-deps

    // 예약건 편집: 비반납 줄별 가용 수량 조회. 기존 줄은 자기 행(excludeId) 제외해 정확히.
    const bookingKey = booking
        ? `${booking.startDate}|${booking.endDate}|${booking.items.map(i => `${i.id || "n"}:${i.equipmentId || ""}:${i.returned ? "R" : ""}`).join(",")}`
        : "";
    useEffect(() => {
        if (!booking || !booking.startDate) { setBookingAvail({}); return; }
        let alive = true;
        Promise.all(booking.items.map(it =>
            (it.equipmentId && !it.returned)
                ? equipmentApi.getAvailability(it.equipmentId, booking.startDate,
                    booking.endDate || booking.startDate, it.id || undefined)
                    .then(d => d?.available ?? 0).catch(() => null)
                : Promise.resolve(null)
        )).then(arr => { if (alive) setBookingAvail(Object.fromEntries(arr.map((v, i) => [i, v]))); });
        return () => { alive = false; };
    }, [bookingKey]);  // eslint-disable-line react-hooks/exhaustive-deps

    // ─── 예약 다이얼로그 ──────────────────────────────────────────
    const openResvNew = (equip) => setResvCart({
        ...emptyCart(),
        deptId: me?.deptId || "",
        applicantName: me?.name || "",
        items: [{ ...emptyItem(), equipmentId: equip ? equip.id : "" }],
    });
    // 예약건(그룹) 상세/수정 다이얼로그 — 같은 groupId 행들을 하나의 예약건으로 연다
    const openBooking = (rows) => {
        const first = rows[0];
        setBooking({
            groupId: first.groupId || null,
            projectName: first.projectName || "",
            projectChannelId: first.projectChannelId || null,
            deptId: first.deptId || "",
            applicantName: first.applicantName || "",
            startDate: first.startDate, endDate: first.endDate,
            memo: rows.map(r => r.memo).find(Boolean) || "",   // 예약건 공통 메모(레거시 대비 첫 값)
            items: rows.map(r => ({
                id: r.id, equipmentId: r.equipmentId || "", equipmentName: r.equipmentName,
                quantity: r.quantity, returned: Boolean(r.returned),
            })),
            allReturned: rows.every(r => r.returned),
        });
    };
    const onBookingProjectChange = (v) => {
        const ch = channels.find(c => c.name === v);
        setBooking(b => ({ ...b, projectName: v || "", projectChannelId: ch ? ch.id : null }));
    };
    const setBookingItem = (idx, patch) =>
        setBooking(b => ({ ...b, items: b.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
    const addBookingItem = () =>
        setBooking(b => ({ ...b, items: [...b.items, { id: null, equipmentId: "", quantity: 1, returned: false }] }));
    const removeBookingItem = (idx) =>
        setBooking(b => ({ ...b, items: b.items.filter((_, i) => i !== idx) }));
    const saveBooking = async () => {
        const b = booking;
        if (!b?.groupId) return;
        const payload = {
            projectName: b.projectName || null, projectChannelId: b.projectChannelId || null,
            deptId: b.deptId || null, applicantName: b.applicantName || null,
            startDate: b.startDate, endDate: b.endDate || b.startDate, memo: b.memo || null,
            // 비반납 항목의 목표 상태(추가·수정·삭제 reconcile). 반납 항목은 서버가 보존.
            items: b.items.filter(it => !it.returned && it.equipmentId).map(it => ({
                id: it.id ?? null, equipmentId: it.equipmentId, quantity: Number(it.quantity) || 1,
            })),
        };
        const res = await runApiAction({
            request: () => equipmentApi.updateReservationGroup(b.groupId, payload),
            successMsg: "예약이 수정되었습니다.", setLoading: setSaving, throwOnError: true,
        });
        if (res.ok) { setBooking(null); reloadReservations(); }
    };
    const deleteBooking = async () => {
        const b = booking;
        if (!b?.groupId) return;
        if (!(await confirmDialog({ message: "이 예약건(전체)을 삭제하시겠습니까?" }))) return;
        const res = await runApiAction({
            request: () => equipmentApi.deleteReservationGroup(b.groupId),
            successMsg: "예약이 삭제되었습니다.", setLoading: setSaving,
        });
        if (res.ok) { setBooking(null); reloadReservations(); }
    };
    const returnBooking = async () => {
        const b = booking;
        if (!b?.groupId) return;
        if (!(await confirmDialog({ message: "이 예약건의 모든 비품을 반납 처리할까요? 반납 즉시 재고로 돌아갑니다." }))) return;
        const res = await runApiAction({
            request: () => equipmentApi.returnReservationGroup(b.groupId),
            successMsg: "반납 처리되었습니다.", setLoading: setSaving,
        });
        if (res.ok) { setBooking(null); reloadReservations(); }
    };
    const returnBookingItem = async (id) => {
        if (!(await confirmDialog({ message: "이 비품을 반납 처리할까요? 반납 즉시 재고로 돌아갑니다." }))) return;
        const res = await runApiAction({
            request: () => equipmentApi.returnReservation(id),
            successMsg: "반납 처리되었습니다.", setLoading: setSaving,
        });
        if (res.ok) {
            setBooking(b => {
                if (!b) return b;
                const items = b.items.map(it => it.id === id ? { ...it, returned: true } : it);
                return { ...b, items, allReturned: items.every(it => it.returned) };
            });
            reloadReservations();
        }
    };
    const deleteBookingItem = async (id) => {
        if (!(await confirmDialog({ message: "이 비품을 예약건에서 삭제할까요?" }))) return;
        const res = await runApiAction({
            request: () => equipmentApi.deleteReservation(id),
            successMsg: "삭제되었습니다.", setLoading: setSaving,
        });
        if (res.ok) {
            setBooking(b => {
                if (!b) return b;
                const items = b.items.filter(it => it.id !== id);
                return items.length ? { ...b, items, allReturned: items.every(it => it.returned) } : null;
            });
            reloadReservations();
        }
    };

    // ─── 장바구니(여러 비품) ──────────────────────────────────────
    const setCart = (patch) => setResvCart(c => ({ ...c, ...patch }));
    const setCartItem = (idx, patch) =>
        setResvCart(c => ({ ...c, items: c.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
    const addCartItem = () => setResvCart(c => ({ ...c, items: [...c.items, emptyItem()] }));
    const removeCartItem = (idx) => setResvCart(c => ({ ...c, items: c.items.filter((_, i) => i !== idx) }));
    const onCartProjectChange = (v) => {
        const ch = channels.find(c => c.name === v);
        setCart({ projectName: v || "", projectChannelId: ch ? ch.id : null });
    };
    const saveCart = async () => {
        const c = resvCart;
        const payload = {
            projectName: c.projectName || null, projectChannelId: c.projectChannelId || null,
            deptId: c.deptId || null, applicantName: c.applicantName || null,
            startDate: c.startDate, endDate: c.endDate || c.startDate,
            memo: c.memo || null,                          // 예약건 전체 메모
            items: c.items.filter(it => it.equipmentId).map(it => ({
                equipmentId: it.equipmentId, quantity: Number(it.quantity) || 1,
            })),
        };
        const res = await runApiAction({
            request: () => equipmentApi.createReservationGroup(payload),
            successMsg: "예약되었습니다.", setLoading: setSaving, throwOnError: true,
        });
        if (res.ok) { setResvCart(null); reloadReservations(); }
    };

    // ─── 비품 등록/수정 다이얼로그 (관리자) ──────────────────────
    const openEquipNew  = () => setEquipForm(emptyEquip());
    const openEquipEdit = (e) => setEquipForm({
        id: e.id, name: e.name, categoryId: e.categoryId || "", locationId: e.locationId || "",
        quantity: e.quantity ?? 1, memo: e.memo || "",
    });
    const saveEquip = async () => {
        const f = equipForm;
        const payload = {
            name: f.name, categoryId: f.categoryId || null, locationId: f.locationId || null,
            quantity: Number(f.quantity) || 1, memo: f.memo || null,
        };
        const res = await runApiAction({
            request: () => f.id ? equipmentApi.updateEquipment(f.id, payload)
                                : equipmentApi.createEquipment(payload),
            successMsg: f.id ? "비품이 수정되었습니다." : "비품이 등록되었습니다.",
            setLoading: setSaving, throwOnError: true,
        });
        if (res.ok) { setEquipForm(null); reloadCatalog(); }
    };
    const deleteEquip = async (e) => {
        if (!(await confirmDialog({ message: `"${e.name}" 비품을 삭제하시겠습니까?` }))) return;
        await runApiAction({ request: () => equipmentApi.deleteEquipment(e.id), successMsg: "삭제되었습니다." });
        reloadCatalog();
    };

    const chOptions = channels.map(c => c.name);
    // 팀 id → 팀명 (신청자·팀 읽기전용 표시용)
    const deptNameOf = (id) => departments.find(d => String(d.deptId) === String(id))?.deptName || "미지정";

    // 장바구니 파생값 — 줄별 초과 여부 + 저장 가능 여부
    const cartItems = resvCart?.items || [];
    const cartLineOver = (i) => {
        const it = cartItems[i]; if (!it?.equipmentId) return false;
        const av = cartAvail[i]; const q = Number(it.quantity) || 0;
        return av != null && q > av;
    };
    const cartValid = Boolean(resvCart) && !!resvCart.startDate
        && cartItems.some(it => it.equipmentId)
        && cartItems.every((it, i) => !it.equipmentId || ((Number(it.quantity) || 0) >= 1 && !cartLineOver(i)));

    // 예약건 편집 파생값 — 줄별 초과 여부 + 저장 가능 여부(반납 줄은 제외)
    const bookingItems = booking?.items || [];
    const bookingLineOver = (i) => {
        const it = bookingItems[i]; if (!it || it.returned || !it.equipmentId) return false;
        const av = bookingAvail[i]; const q = Number(it.quantity) || 0;
        return av != null && q > av;
    };
    const bookingValid = Boolean(booking) && !!booking.startDate
        && (bookingItems.some(it => it.returned) || bookingItems.some(it => !it.returned && it.equipmentId))
        && bookingItems.every((it, i) => it.returned || !it.equipmentId || ((Number(it.quantity) || 0) >= 1 && !bookingLineOver(i)));

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1500, mx: "auto" }}>
            {/* 헤더 */}
            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" mb={2}>
                <Typography sx={ds.typography.pageTitle}>비품 예약</Typography>
                <Typography sx={{ ...ds.typography.caption, fontSize: 12.5 }}>사내 비품을 예약하고 현황을 확인합니다</Typography>
                <Box sx={{ flex: 1 }} />
                {canManage && (
                    <ActionButton variant="secondary" startIcon={<SettingsRoundedIcon sx={{ fontSize: 15 }} />}
                        onClick={() => setCatalogOpen(true)} label="카테고리·보관위치" sx={{ fontSize: 12 }} />
                )}
                {canManage && (
                    <ActionButton variant="secondary" startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                        onClick={openEquipNew} label="비품 등록" sx={{ fontSize: 12 }} />
                )}
                <ActionButton variant="add" startIcon={<EventAvailableRoundedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => openResvNew(null)} label="예약하기" />
            </Stack>

            {loading ? <LoadingDots /> : (
                <Box sx={{ display: "flex", gap: 2, alignItems: "stretch", flexWrap: { xs: "wrap", lg: "nowrap" } }}>
                    {/* 중앙: 예약 현황 캘린더 */}
                    <Paper sx={{ ...ds.cardPadded, p: 2, flex: 1, minWidth: 0 }}>
                        <BaseCalendar
                            anchor={anchor} onAnchorChange={setAnchor}
                            view="month" supportedViews={["month"]}
                            spans={spans}
                            toolbarRight={
                                <Stack direction="row" gap={0.5}>
                                    <Box sx={{ px: 0.8, py: 0.25, borderRadius: 0.5, bgcolor: RESV_COLOR.bg,
                                        borderLeft: `3px solid ${RESV_COLOR.bar}` }}>
                                        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: RESV_COLOR.text }}>비품 예약</Typography>
                                    </Box>
                                    <Box sx={{ px: 0.8, py: 0.25, borderRadius: 0.5, bgcolor: RESV_RETURNED_COLOR.bg,
                                        borderLeft: `3px solid ${RESV_RETURNED_COLOR.bar}` }}>
                                        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: RESV_RETURNED_COLOR.text }}>반납완료</Typography>
                                    </Box>
                                </Stack>
                            }
                        />
                    </Paper>

                    {/* 우측: 비품 목록 (2열 그리드로 표시 — 39개도 절반 길이로).
                        데스크톱(lg)에서는 좌측 캘린더와 높이를 맞추기 위해 flex 컬럼으로 만들고
                        목록 영역이 남는 높이를 채우며 내부 스크롤한다(아래 목록 Box의 flex:1). */}
                    <Paper sx={{ ...ds.cardPadded, p: 2, width: { xs: "100%", lg: 500 },
                        flexShrink: 0, alignSelf: "stretch",
                        display: { xs: "block", lg: "flex" }, flexDirection: { lg: "column" }, minHeight: 0 }}>
                        <Stack direction="row" alignItems="center" mb={1.5}>
                            <Typography sx={{ fontSize: 14, fontWeight: 800 }}>비품 목록</Typography>
                            <Chip label={`${filteredEquipment.length}`} size="small"
                                sx={{ ml: 1, height: 20, fontSize: 11, bgcolor: C.borderLight }} />
                            <Typography sx={{ ml: "auto", fontSize: 12, color: C.textMuted }}>
                                오늘({dayjs().format("YYYY.MM.DD")}) 날짜 기준으로 수량을 계산합니다
                            </Typography>

                        </Stack>
                        <SearchField value={search} onChange={setSearch} placeholder="비품·카테고리·위치 검색" width="100%" />
                        {/* 카테고리 필터 칩 */}
                        <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap", my: 1.2 }}>
                            <Chip label="전체" size="small" onClick={() => setCatFilter("")}
                                variant={catFilter === "" ? "filled" : "outlined"}
                                sx={{ height: 24, fontSize: 11.5, fontWeight: 700,
                                    ...(catFilter === "" ? { bgcolor: C.textPrimary, color: "#fff" } : {}) }} />
                            {categories.map(c => {
                                const col = catColor(c.id); const on = String(catFilter) === String(c.id);
                                return (
                                    <Chip key={c.id} label={c.name} size="small" onClick={() => setCatFilter(on ? "" : c.id)}
                                        sx={{ height: 24, fontSize: 11.5, fontWeight: 700, cursor: "pointer",
                                            bgcolor: on ? col.fg : col.bg, color: on ? "#fff" : col.fg }} />
                                );
                            })}
                        </Box>
                        <Divider sx={{ mb: 1 }} />
                        {/* 목록 — lg: 스크롤 콘텐츠를 absolute로 흐름에서 빼 '컨테이너 높이 = 목록 콘텐츠'
                            순환을 끊는다. 그래야 flex 라인 높이가 좌측 캘린더로 정해지고, 목록은 남는
                            높이만큼만 차지하며 내부 스크롤한다 → 좌우 카드 높이가 일치.
                            xs(모바일): position static + maxHeight 620 스크롤(기존 동작 유지). */}
                        <Box sx={{ position: { lg: "relative" }, flex: { lg: 1 }, minHeight: { lg: 0 } }}>
                          <Box sx={{ position: { lg: "absolute" }, inset: { lg: 0 },
                              overflowY: "auto", maxHeight: { xs: 620, lg: "none" }, pr: 0.5 }}>
                            {filteredEquipment.length === 0 ? (
                                <Typography sx={{ py: 4, textAlign: "center", fontSize: 13, color: C.textMuted }}>
                                    {equipment.length === 0
                                        ? (canManage ? "등록된 비품이 없습니다. [비품 등록]으로 추가하세요." : "등록된 비품이 없습니다.")
                                        : "조건에 맞는 비품이 없습니다."}
                                </Typography>
                            ) : (
                              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                  gap: 1, alignContent: "start" }}>
                                {filteredEquipment.map(e => {
                                const col = catColor(e.categoryId);
                                // 여유(오늘 재고) 0개면 카드 회색 처리
                                const soldOut = Math.max(0, (e.quantity ?? 0) - (e.reservedToday || 0)) <= 0;
                                return (
                                    <Box key={e.id} sx={{
                                        p: 1.2, borderRadius: 1.5, border: "1px solid #eef2f7", minWidth: 0,
                                        ...(soldOut && { bgcolor: C.borderLight, opacity: 0.6 }),
                                        "&:hover": { borderColor: "#cbd5e1", bgcolor: soldOut ? C.borderLight : C.rowHover },
                                        "&:hover .eq-actions": { opacity: 1 },
                                    }}>
                                        <Stack direction="row" alignItems="flex-start" gap={2}>
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{e.name}</Typography>
                                                {/* 카테고리는 비품명 아래 줄에 표시(여유 수량과 함께) */}
                                                <Stack direction="row" alignItems="center" gap={0.6} flexWrap="wrap" sx={{ mt: 0.4 }}>
                                                    {e.categoryName && (
                                                            <Chip label={e.categoryName} size="small" sx={{ height: 18, fontSize: 10, bgcolor: col.bg, color: col.fg, fontWeight: 700 }} />
                                                    )}
                                                    {(() => {
                                                        const total = e.quantity ?? 0;
                                                        const avail = Math.max(0, total - (e.reservedToday || 0));
                                                        // 재고 0 → 빨강, 여유가 총수량(가득) → 초록, 그 외(일부 예약) → 파랑
                                                        const tone = avail <= 0
                                                            ? { bg: C.errorBg, fg: C.error }
                                                            : avail >= total ? { bg: C.successBg, fg: C.success }
                                                            : { bg: C.infoBg, fg: C.info };
                                                        return (
                                                            <Chip label={`여유 ${avail}/${total}개`} size="small"
                                                                sx={{ height: 18, fontSize: 10, fontWeight: 700,
                                                                    bgcolor: tone.bg, color: tone.fg }} />
                                                        );
                                                    })()}
                                                </Stack>
                                                <Stack direction="row" alignItems="center" gap={0.4} sx={{ mt: 0.4 }}>
                                                    <PlaceRoundedIcon sx={{ fontSize: 13, color: C.textMuted }} />
                                                    <Typography sx={{ fontSize: 11.5, color: C.secondary }}>{e.locationName || "위치 미지정"}</Typography>
                                                </Stack>
                                                {e.memo && (
                                                    <Typography noWrap sx={{ fontSize: 11, color: C.textMuted, mt: 0.2 }} title={e.memo}>{e.memo}</Typography>
                                                )}
                                            </Box>
                                            <Stack alignItems="flex-end" gap={0.5}>
                                                <ActionButton variant="secondary" onClick={() => openResvNew(e)} disabled={soldOut}
                                                    label="예약" sx={{ fontSize: 11, minWidth: "auto", px: 1.2, py: 0.3 }} />
                                                {canManage && (
                                                    <Box className="eq-actions" sx={{ opacity: 0, transition: "opacity 0.12s", display: "flex" }}>
                                                        <Tooltip title="수정"><IconButton size="small" onClick={() => openEquipEdit(e)}>
                                                            <EditRoundedIcon sx={{ fontSize: 15, color: C.secondary }} /></IconButton></Tooltip>
                                                        <Tooltip title="삭제"><IconButton size="small" onClick={() => deleteEquip(e)}>
                                                            <DeleteOutlineRoundedIcon sx={{ fontSize: 15, color: C.error }} /></IconButton></Tooltip>
                                                    </Box>
                                                )}
                                            </Stack>
                                        </Stack>
                                    </Box>
                                );
                                })}
                              </Box>
                            )}
                          </Box>
                        </Box>
                    </Paper>
                </Box>
            )}

            {/* ── 예약건(그룹) 상세/수정 다이얼로그 ── */}
            <FormModal open={Boolean(booking)} onClose={() => setBooking(null)}
                title={booking?.allReturned ? "예약 상세 · 반납완료" : "예약 상세"} maxWidth="sm"
                onSave={booking?.groupId ? saveBooking : undefined} saving={saving}
                saveDisabled={!bookingValid} saveLabel="저장"
                footerExtras={booking ? (
                    <Stack direction="row" gap={1}>
                        {!booking.allReturned && (
                            <ActionButton variant="secondary" onClick={returnBooking} label="반납 처리"
                                sx={{ fontSize: 12 }} />
                        )}
                        <ActionButton variant="secondary" onClick={deleteBooking} label="삭제"
                            sx={{ fontSize: 12, color: C.error }} />
                    </Stack>
                ) : null}>
                {booking && (
                    <Stack spacing={2}>
                        <FormField label="프로젝트">
                            <ComboBox freeSolo placeholder="프로젝트 채널에서 선택하거나 직접 입력"
                                options={chOptions} value={booking.projectName}
                                onChange={onBookingProjectChange} />
                        </FormField>
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            {/* 신청자·팀은 본인으로 고정 — 수정 불가(읽기전용) */}
                            <FormField label="팀" sx={{ flex: 1, minWidth: 0 }}>
                                <TextField fullWidth size="small" disabled value={deptNameOf(booking.deptId)} />
                            </FormField>
                            <FormField label="신청자" sx={{ flex: 1, minWidth: 0 }}>
                                <TextField fullWidth size="small" disabled value={booking.applicantName || "—"} />
                            </FormField>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            <FormField label="시작일" required sx={{ flex: 1, minWidth: 0 }}>
                                <TextField fullWidth size="small" type="date" value={booking.startDate}
                                    onChange={e => setBooking(b => ({ ...b, startDate: e.target.value,
                                        endDate: b.endDate < e.target.value ? e.target.value : b.endDate }))} />
                            </FormField>
                            <FormField label="종료일" required sx={{ flex: 1, minWidth: 0 }}>
                                <TextField fullWidth size="small" type="date" value={booking.endDate}
                                    inputProps={{ min: booking.startDate }}
                                    onChange={e => setBooking(b => ({ ...b, endDate: e.target.value }))} />
                            </FormField>
                        </Box>
                        <Divider textAlign="left">
                            <Typography sx={{ fontSize: 12, color: C.textMuted }}>예약 비품 {booking.items.length}건</Typography>
                        </Divider>
                        <Stack spacing={1.5}>
                            {bookingItems.map((it, idx) => {
                                // 반납 완료 항목 — 읽기전용 + 삭제만
                                if (it.returned) return (
                                    <Box key={it.id} sx={{ display: "flex", alignItems: "center", gap: 1,
                                        p: 1, borderRadius: 1.5, border: "1px solid #eef2f7", bgcolor: RESV_RETURNED_COLOR.bg }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Stack direction="row" alignItems="center" gap={0.6} flexWrap="wrap">
                                                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{it.equipmentName}</Typography>
                                                <Chip label={`${it.quantity}개`} size="small"
                                                    sx={{ height: 18, fontSize: 10, bgcolor: "#fff" }} />
                                                <Chip label="반납완료" size="small"
                                                    sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#fff", color: RESV_RETURNED_COLOR.text }} />
                                            </Stack>
                                        </Box>
                                        <Tooltip title="이 비품 예약 삭제"><IconButton size="small" onClick={() => deleteBookingItem(it.id)}>
                                            <DeleteOutlineRoundedIcon sx={{ fontSize: 16, color: C.error }} /></IconButton></Tooltip>
                                    </Box>
                                );
                                // 비반납 항목 — 비품·수량 편집(저장 시 반영), 즉시 반납, 줄 삭제
                                const eq = equipment.find(e => String(e.id) === String(it.equipmentId));
                                const av = bookingAvail[idx];
                                const over = bookingLineOver(idx);
                                return (
                                    <Box key={it.id ?? `new-${idx}`} sx={{ p: 1.2, borderRadius: 1.5, border: `1px solid ${over ? C.error : "#eef2f7"}` }}>
                                        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                                            <FormField label="비품" required sx={{ flex: 2, minWidth: 0 }}>
                                                <ComboBox placeholder="비품 선택"
                                                    options={equipment.map(e => ({ value: e.id,
                                                        label: `${e.name}${e.categoryName ? ` (${e.categoryName})` : ""}` }))}
                                                    value={it.equipmentId}
                                                    onChange={v => setBookingItem(idx, { equipmentId: v })} />
                                            </FormField>
                                            <FormField label="수량" required sx={{ flex: 1, minWidth: 76 }}>
                                                <TextField fullWidth size="small" type="number" value={it.quantity} error={over}
                                                    inputProps={{ min: 1, ...(av != null ? { max: Math.max(1, av) } : {}) }}
                                                    InputProps={{ endAdornment: <InputAdornment position="end">개</InputAdornment> }}
                                                    onChange={e => setBookingItem(idx, { quantity: e.target.value })} />
                                            </FormField>
                                            {it.id && (
                                                <Tooltip title="이 비품 반납"><IconButton size="small" sx={{ mt: 2.4 }} onClick={() => returnBookingItem(it.id)}>
                                                    <AssignmentReturnedRoundedIcon sx={{ fontSize: 17, color: C.secondary }} /></IconButton></Tooltip>
                                            )}
                                            {bookingItems.length > 1 && (
                                                <IconButton size="small" sx={{ mt: 2.4 }} onClick={() => removeBookingItem(idx)}>
                                                    <DeleteOutlineRoundedIcon sx={{ fontSize: 17, color: C.error }} />
                                                </IconButton>
                                            )}
                                        </Box>
                                        {it.equipmentId && (
                                            <Typography sx={{ fontSize: 11.5, mt: 0.3, color: over ? C.error : C.secondary }}>
                                                이 기간 예약 가능{" "}
                                                <b style={{ color: over ? C.error : C.textPrimary }}>{av == null ? "…" : `${av}개`}</b>
                                                {eq ? ` / 총 ${eq.quantity ?? 0}개` : ""}{over ? " — 초과" : ""}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                            <ActionButton variant="secondary" startIcon={<AddRoundedIcon sx={{ fontSize: 15 }} />}
                                onClick={addBookingItem} label="비품 추가" sx={{ fontSize: 12, alignSelf: "flex-start" }} />
                        </Stack>
                        <FormField label="메모">
                            <TextField fullWidth size="small" multiline rows={2} value={booking.memo}
                                placeholder="예약건 전체에 대한 메모"
                                onChange={e => setBooking(b => ({ ...b, memo: e.target.value }))} />
                        </FormField>
                    </Stack>
                )}
            </FormModal>

            {/* ── 여러 비품 예약(장바구니) 다이얼로그 ── */}
            <FormModal open={Boolean(resvCart)} onClose={() => setResvCart(null)}
                title="비품 예약" maxWidth="sm"
                onSave={saveCart} saving={saving}
                saveDisabled={!cartValid} saveLabel="예약">
                {resvCart && (
                    <Stack spacing={2}>
                        <FormField label="프로젝트">
                            <ComboBox freeSolo placeholder="프로젝트 채널에서 선택하거나 직접 입력"
                                options={chOptions} value={resvCart.projectName} onChange={onCartProjectChange} />
                        </FormField>
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            {/* 신청자·팀은 로그인 사용자 본인으로 고정 — 수정 불가(읽기전용) */}
                            <FormField label="팀" sx={{ flex: 1, minWidth: 0 }}>
                                <TextField fullWidth size="small" disabled value={deptNameOf(resvCart.deptId)} />
                            </FormField>
                            <FormField label="신청자" sx={{ flex: 1, minWidth: 0 }}>
                                <TextField fullWidth size="small" disabled value={resvCart.applicantName || "—"} />
                            </FormField>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            <FormField label="시작일" required sx={{ flex: 1, minWidth: 0 }}>
                                <TextField fullWidth size="small" type="date" value={resvCart.startDate}
                                    inputProps={{ min: todayStr() }}
                                    onChange={e => setCart({ startDate: e.target.value,
                                        endDate: resvCart.endDate < e.target.value ? e.target.value : resvCart.endDate })} />
                            </FormField>
                            <FormField label="종료일" required sx={{ flex: 1, minWidth: 0 }}>
                                <TextField fullWidth size="small" type="date" value={resvCart.endDate}
                                    inputProps={{ min: resvCart.startDate }}
                                    onChange={e => setCart({ endDate: e.target.value })} />
                            </FormField>
                        </Box>
                        <Divider textAlign="left">
                            <Typography sx={{ fontSize: 12, color: C.textMuted }}>예약 비품</Typography>
                        </Divider>
                        <Stack spacing={1.5}>
                            {cartItems.map((it, idx) => {
                                const eq = equipment.find(e => String(e.id) === String(it.equipmentId));
                                const av = cartAvail[idx];
                                const over = cartLineOver(idx);
                                return (
                                    <Box key={idx} sx={{ p: 1.2, borderRadius: 1.5, border: `1px solid ${over ? C.error : "#eef2f7"}` }}>
                                        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                                            <FormField label={`비품 ${idx + 1}`} required sx={{ flex: 2, minWidth: 0 }}>
                                                <ComboBox placeholder="비품 선택"
                                                    options={equipment.map(e => ({ value: e.id,
                                                        label: `${e.name}${e.categoryName ? ` (${e.categoryName})` : ""}` }))}
                                                    value={it.equipmentId}
                                                    onChange={v => setCartItem(idx, { equipmentId: v })} />
                                            </FormField>
                                            <FormField label="수량" required sx={{ flex: 1, minWidth: 80 }}>
                                                <TextField fullWidth size="small" type="number" value={it.quantity} error={over}
                                                    inputProps={{ min: 1, ...(av != null ? { max: Math.max(1, av) } : {}) }}
                                                    InputProps={{ endAdornment: <InputAdornment position="end">개</InputAdornment> }}
                                                    onChange={e => setCartItem(idx, { quantity: e.target.value })} />
                                            </FormField>
                                            {cartItems.length > 1 && (
                                                <IconButton size="small" sx={{ mt: 2.4 }} onClick={() => removeCartItem(idx)}>
                                                    <DeleteOutlineRoundedIcon sx={{ fontSize: 17, color: C.error }} />
                                                </IconButton>
                                            )}
                                        </Box>
                                        {it.equipmentId && (
                                            <Typography sx={{ fontSize: 11.5, mt: 0.3, color: over ? C.error : C.secondary }}>
                                                이 기간 예약 가능{" "}
                                                <b style={{ color: over ? C.error : C.textPrimary }}>{av == null ? "…" : `${av}개`}</b>
                                                {eq ? ` / 총 ${eq.quantity ?? 0}개` : ""}{over ? " — 초과" : ""}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                            <ActionButton variant="secondary" startIcon={<AddRoundedIcon sx={{ fontSize: 15 }} />}
                                onClick={addCartItem} label="비품 추가" sx={{ fontSize: 12, alignSelf: "flex-start" }} />
                        </Stack>
                        <FormField label="메모">
                            <TextField fullWidth size="small" multiline rows={2} value={resvCart.memo}
                                placeholder="예약건 전체에 대한 메모(선택)"
                                onChange={e => setCart({ memo: e.target.value })} />
                        </FormField>
                    </Stack>
                )}
            </FormModal>

            {/* ── 비품 등록/수정 다이얼로그 ── */}
            <FormModal open={Boolean(equipForm)} onClose={() => setEquipForm(null)}
                title={equipForm?.id ? "비품 수정" : "비품 등록"} maxWidth="sm"
                onSave={saveEquip} saving={saving}
                saveDisabled={!equipForm?.name} saveLabel={equipForm?.id ? "수정" : "등록"}>
                {equipForm && (
                    <Stack spacing={2}>
                        <FormField label="비품 이름" required>
                            <TextField fullWidth size="small" value={equipForm.name}
                                placeholder="예: 소니 A7M4, 무선마이크 세트"
                                onChange={e => setEquipForm(f => ({ ...f, name: e.target.value }))} />
                        </FormField>
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            <FormField label="카테고리" sx={{ flex: 1, minWidth: 0 }}>
                                <ComboBox placeholder="선택 안 함"
                                    options={categories.map(c => ({ value: c.id, label: c.name }))}
                                    value={equipForm.categoryId}
                                    onChange={v => setEquipForm(f => ({ ...f, categoryId: v }))} />
                            </FormField>
                            <FormField label="보관위치" sx={{ flex: 1, minWidth: 0 }}>
                                <ComboBox placeholder="선택 안 함"
                                    options={locations.map(l => ({ value: l.id, label: l.name }))}
                                    value={equipForm.locationId}
                                    onChange={v => setEquipForm(f => ({ ...f, locationId: v }))} />
                            </FormField>
                        </Box>
                        <FormField label="수량">
                            <TextField size="small" type="number" value={equipForm.quantity}
                                inputProps={{ min: 1 }} sx={{ width: 140 }}
                                InputProps={{ endAdornment: <InputAdornment position="end">개</InputAdornment> }}
                                onChange={e => setEquipForm(f => ({ ...f, quantity: e.target.value }))} />
                        </FormField>
                        <FormField label="메모">
                            <TextField fullWidth size="small" multiline rows={2} value={equipForm.memo}
                                onChange={e => setEquipForm(f => ({ ...f, memo: e.target.value }))} />
                        </FormField>
                    </Stack>
                )}
            </FormModal>

            {/* ── 카테고리·보관위치 관리 다이얼로그 ── */}
            <CatalogAdminModal open={catalogOpen} onClose={() => setCatalogOpen(false)}
                categories={categories} locations={locations} onChanged={reloadCatalog} />
        </Box>
    );
}

/** 카테고리·보관위치 관리(추가·수정·사용여부) — 관리자 전용. */
function CatalogAdminModal({ open, onClose, categories, locations, onChanged }) {
    const [tab, setTab] = useState("category");
    const [draft, setDraft] = useState("");   // 새 항목 이름

    const rows = tab === "category" ? categories : locations;
    const create = tab === "category" ? equipmentApi.createCategory : equipmentApi.createLocation;
    const remove = tab === "category" ? equipmentApi.deleteCategory : equipmentApi.deleteLocation;
    const label  = tab === "category" ? "카테고리" : "보관위치";

    const add = async () => {
        if (!draft.trim()) return;
        await runApiAction({ request: () => create({ name: draft.trim() }), successMsg: `${label} 추가됨`, throwOnError: true });
        setDraft(""); onChanged?.();
    };
    const del = async (row) => {
        if (!(await confirmDialog({ message: `"${row.name}" ${label}을(를) 삭제하시겠습니까?` }))) return;
        await runApiAction({ request: () => remove(row.id), successMsg: "삭제됨" });
        onChanged?.();
    };

    return (
        <FormModal open={open} onClose={onClose} title="카테고리 · 보관위치 관리" maxWidth="sm">
            <Stack spacing={1.5}>
                <Stack direction="row" gap={1}>
                    {[["category", "카테고리"], ["location", "보관위치"]].map(([k, lbl]) => (
                        <Chip key={k} label={lbl} onClick={() => setTab(k)}
                            sx={{ fontWeight: 700, cursor: "pointer",
                                bgcolor: tab === k ? C.textPrimary : C.borderLight, color: tab === k ? "#fff" : C.textSecondary }} />
                    ))}
                </Stack>
                <Stack direction="row" gap={1}>
                    <TextField size="small" fullWidth placeholder={`새 ${label} 이름`} value={draft}
                        onChange={e => setDraft(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") add(); }} />
                    <ActionButton variant="add" onClick={add} label="추가" disabled={!draft.trim()} />
                </Stack>
                <Divider />
                <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
                    {rows.map(row => (
                        <Stack key={row.id} direction="row" alignItems="center" gap={1} sx={{ py: 0.6 }}>
                            <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.textPrimary }}>
                                {row.name}
                            </Typography>
                            <Tooltip title="삭제"><IconButton size="small" onClick={() => del(row)}>
                                <DeleteOutlineRoundedIcon sx={{ fontSize: 16, color: C.error }} /></IconButton></Tooltip>
                        </Stack>
                    ))}
                    {rows.length === 0 && (
                        <Typography sx={{ py: 3, textAlign: "center", fontSize: 12.5, color: C.textMuted }}>항목이 없습니다.</Typography>
                    )}
                </Box>
            </Stack>
        </FormModal>
    );
}
